import { useState, useEffect, useCallback } from "react";
import { Copy, Download, Loader2 } from "lucide-react";
import { Shimmer, TableShimmer } from "@/components/ui/shimmer";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DataTablePagination } from "@/components/ui/data-table-pagination";
import TransactionFiltersPanel, {
  getDefaultFilters,
  type FilterValues,
} from "@/components/common/TransactionFiltersPanel";
import { useAuth } from "@/context/AuthContext";
import {
  getSharedSubOrders,
  getAllSharedSubOrders,
  getSharedSubOrderStatuses,
  type SharedSubOrderRow,
  type SharedSubOrderFilters,
} from "@/utils/shared-sub-orders-api";
import { exportSharedSubOrdersToExcel } from "@/utils/export-excel";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const statusBadgeClass: Record<string, string> = {
  active: "border-green-500 text-green-600 dark:text-green-400",
  allocated: "border-green-500 text-green-600 dark:text-green-400",
  pending: "border-yellow-500 text-yellow-600 dark:text-yellow-400",
  processing: "border-blue-500 text-blue-600 dark:text-blue-400",
  refunded: "border-red-500 text-red-500 dark:text-red-400",
  cancelled: "border-red-500 text-red-500 dark:text-red-400",
  expired: "",
};

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export default function SharedSubscriptionsOrdersPage() {
  const { userId, token } = useAuth();

  const [rows, setRows] = useState<SharedSubOrderRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState<FilterValues>(getDefaultFilters);
  const [availableStatuses, setAvailableStatuses] = useState<string[]>([]);

  useEffect(() => {
    if (!userId || !token) return;
    getSharedSubOrderStatuses(userId, token).then(setAvailableStatuses).catch(() => {});
  }, [userId, token]);

  const buildFilters = useCallback((): SharedSubOrderFilters => {
    const f: SharedSubOrderFilters = { userId: userId! };
    if (filters.selectedTypes.length > 0) f.statuses = filters.selectedTypes;
    if (filters.createdFrom) f.createdFrom = filters.createdFrom.startOf("day").toISOString();
    if (filters.createdTo) f.createdTo = filters.createdTo.endOf("day").toISOString();
    if (filters.search.trim()) f.search = filters.search.trim();
    return f;
  }, [userId, filters]);

  const fetchData = useCallback(async () => {
    if (!userId || !token) return;
    setLoading(true);
    setError("");
    try {
      const result = await getSharedSubOrders(buildFilters(), rowsPerPage, page * rowsPerPage, token);
      setRows(result.rows);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, [userId, token, page, rowsPerPage, buildFilters]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(0); }, [filters]);

  const handleExport = async () => {
    if (!userId || !token) return;
    setExporting(true);
    try {
      const allRows = await getAllSharedSubOrders(buildFilters(), token);
      exportSharedSubOrdersToExcel(allRows);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const copyText = (text: string) => { navigator.clipboard.writeText(text); };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold">
          Shared Subscriptions &mdash; Order History
        </h1>
        <p className="text-muted-foreground">
          View all shared subscription slot purchases and their statuses.
        </p>
      </div>

      <Shimmer loading={loading} fallback={<TableShimmer cols={7} />}>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <div>
                <h3 className="text-base font-semibold">Subscription Orders</h3>
                <span className="text-sm text-muted-foreground">
                  {total.toLocaleString()} orders total
                </span>
              </div>
            </div>

            <TransactionFiltersPanel
              filters={filters}
              onChange={setFilters}
              availableTypes={availableStatuses}
              actions={
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExport}
                  disabled={exporting || total === 0}
                >
                  {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  {exporting ? "Exporting..." : "Export Excel"}
                </Button>
              }
            />

            {error && <Alert variant="destructive" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-semibold">Short ID</TableHead>
                  <TableHead className="font-semibold">Service</TableHead>
                  <TableHead className="font-semibold">Plan</TableHead>
                  <TableHead className="font-semibold text-right">Price</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold">Allocated</TableHead>
                  <TableHead className="font-semibold">Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12">
                      <span className="text-muted-foreground">No orders found</span>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <span className="text-xs font-mono">
                            {order.short_id}
                          </span>
                          <Button variant="ghost" size="icon-xs" onClick={() => copyText(order.id)}>
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-7 w-7 rounded-md">
                            <AvatarImage src={order.whatsub_plan?.whatsub_service?.image_url} />
                            <AvatarFallback className="rounded-md">
                              {(order.whatsub_plan?.whatsub_service?.service_name ?? "?")[0]}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-sm font-medium max-w-[140px] truncate" title={order.whatsub_plan?.whatsub_service?.service_name ?? "—"}>
                            {order.whatsub_plan?.whatsub_service?.service_name ?? "—"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-muted-foreground max-w-[160px] truncate block">
                          {order.whatsub_plan?.plan_name ?? "—"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-sm font-semibold">
                          ₹{order.price.toLocaleString("en-IN")}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={statusBadgeClass[order.status] ?? ""}
                        >
                          {capitalize(order.status)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-muted-foreground">
                          {order.allocated_at ? formatDate(order.allocated_at) : "—"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-muted-foreground">
                          {formatDate(order.created_at)}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <DataTablePagination
              count={total}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={(p) => setPage(p)}
              onRowsPerPageChange={(rpp) => { setRowsPerPage(rpp); setPage(0); }}
            />
          </CardContent>
        </Card>
      </Shimmer>
    </div>
  );
}
